/**
 * Step 1: File Upload
 *
 * Allows user to upload CSV file with validation.
 */
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { Upload, FileText, AlertCircle, CheckCircle2, X } from 'lucide-react';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ROWS = 2000;

export default function FileUploadStep({ onNext, onDataChange, onFileChange, initialFile, initialCsvData }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(initialFile || null);
  const [csvData, setCsvData] = useState(initialCsvData || null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file type
    if (!file.name.endsWith('.csv')) {
      return t('bulkUpload.errors.invalidFileType');
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return t('bulkUpload.errors.fileTooLarge', {
        size: (file.size / (1024 * 1024)).toFixed(2)
      });
    }

    return null;
  };

  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(), // Remove BOM and trim
        complete: (results) => {
          // Check row count
          if (results.data.length > MAX_ROWS) {
            reject(new Error(
              t('bulkUpload.errors.tooManyRows', {
                count: results.data.length,
                max: MAX_ROWS
              })
            ));
            return;
          }

          // Check if CSV has data
          if (results.data.length === 0) {
            reject(new Error(t('bulkUpload.errors.emptyFile')));
            return;
          }

          // Check if CSV has columns
          if (!results.meta.fields || results.meta.fields.length === 0) {
            reject(new Error(t('bulkUpload.errors.noColumns')));
            return;
          }

          resolve({
            data: results.data,
            columns: results.meta.fields,
            rowCount: results.data.length,
          });
        },
        error: (error) => {
          reject(new Error(t('bulkUpload.errors.parseError', {
            message: error.message
          })));
        },
      });
    });
  };

  const handleFileSelect = async (selectedFile) => {
    setError(null);
    setIsProcessing(true);

    try {
      // Validate file
      const validationError = validateFile(selectedFile);
      if (validationError) {
        setError(validationError);
        setIsProcessing(false);
        return;
      }

      // Parse CSV
      const parsedData = await parseCSV(selectedFile);

      // Update state
      setFile(selectedFile);
      setCsvData(parsedData);
      onFileChange(selectedFile);
      onDataChange(parsedData);
    } catch (err) {
      setError(err.message);
      setFile(null);
      setCsvData(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setCsvData(null);
    setError(null);
    onFileChange(null);
    onDataChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleNext = () => {
    if (csvData) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      {/* Instructions - Only show when no file uploaded */}
      {!file && (
        <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            {t('bulkUpload.steps.upload.instructions.title')}
          </h3>
          <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li>{t('bulkUpload.steps.upload.instructions.csvFormat')}</li>
            <li>{t('bulkUpload.steps.upload.instructions.maxSize')}</li>
            <li>{t('bulkUpload.steps.upload.instructions.maxRows')}</li>
            <li>{t('bulkUpload.steps.upload.instructions.encoding')}</li>
          </ul>
        </div>
      )}

      {/* File Upload Area */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-12
            flex flex-col items-center justify-center
            cursor-pointer transition-colors
            ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20' : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'}
            ${isProcessing ? 'opacity-50 cursor-wait' : ''}
          `}
        >
          <Upload className={`w-16 h-16 mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'}`} />

          <p className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-200">
            {t('bulkUpload.steps.upload.dropzone.title')}
          </p>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('bulkUpload.steps.upload.dropzone.subtitle')}
          </p>

          <Button variant="outline" disabled={isProcessing}>
            {isProcessing ? t('common.processing') : t('bulkUpload.steps.upload.dropzone.button')}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}

      {/* File Info Display */}
      {file && csvData && (
        <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="bg-green-100 dark:bg-green-900/40 p-3 rounded-lg">
                <FileText className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>

              <div>
                <h4 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                  {file.name}
                </h4>

                <div className="text-sm text-green-700 dark:text-green-300">
                  {t('bulkUpload.steps.upload.fileInfo.size')}: {(file.size / 1024).toFixed(2)} KB • {t('bulkUpload.steps.upload.fileInfo.rows')}: {csvData.rowCount} • {t('bulkUpload.steps.upload.fileInfo.columns')}: {csvData.columns.length}
                </div>

                {/* Column Preview */}
                <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-green-200 dark:border-green-800">
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">
                    {t('bulkUpload.steps.upload.fileInfo.columnsPreview')}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {csvData.columns.slice(0, 10).map((col, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded"
                      >
                        {col}
                      </span>
                    ))}
                    {csvData.columns.length > 10 && (
                      <span className="text-xs px-2 py-1 text-gray-500">
                        +{csvData.columns.length - 10} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleRemoveFile}
              className="text-green-700 hover:text-green-900"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Success Message */}
      {csvData && !error && (
        <Alert className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            {t('bulkUpload.steps.upload.success', { count: csvData.rowCount })}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
